
import React from 'react';
import { CardContent } from "@/components/ui/card";
import { ExternalLink, LockKeyhole } from 'lucide-react';
import { ClinicalDetails, AdverseReactions } from '@/data/medications';
import ClinicalDetailsSection from './ClinicalDetailsSection';
import YouTubeEmbed from '../resources/YouTubeEmbed';
import { Button } from '@/components/ui/button';

interface MedicationCardContentProps {
  description: string;
  research: string;
  url: string;
  video?: string;
  isAuthenticated: boolean;
  isProviderView: boolean;
  clinicalDetails?: ClinicalDetails;
  availablePreparations?: string;
  adverseReactions?: AdverseReactions;
  category?: string;
  name?: string;
}

const MedicationCardContent: React.FC<MedicationCardContentProps> = ({ 
  description,
  research,
  url,
  video,
  isAuthenticated,
  isProviderView,
  clinicalDetails,
  availablePreparations,
  adverseReactions,
  category,
  name
}) => {
  const showClinicalDetails = isAuthenticated && isProviderView && (clinicalDetails || availablePreparations || adverseReactions);
  const showVideo = video && category === 'weight-loss';
  
  return (
    <CardContent className="p-4 md:p-6 flex flex-col space-y-4">
      {showVideo && (
        <div className="w-full">
          <YouTubeEmbed 
            embedUrl={video} 
            title={`Medication Video`} 
            className="rounded-md"
          />
        </div>
      )}
      
      <div>
        <p className="text-base md:text-lg text-foreground mb-3">{description}</p>
        
        <p className="text-sm md:text-base text-muted-foreground">
          {research} 
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="medication-link inline-flex items-center ml-1"
          >
            Learn More <ExternalLink className="ml-1 h-3 w-3 md:h-4 md:w-4" />
          </a>
        </p>
      </div>
      
      {showClinicalDetails && (
        <div className="pt-4 border-t border-slate-200">
          <ClinicalDetailsSection 
            clinicalDetails={clinicalDetails} 
            adverseReactions={adverseReactions}
            medicationName={name} 
          />
        </div>
      )}
      
      {isAuthenticated && !isProviderView && (clinicalDetails || availablePreparations || adverseReactions) && (
        <div className="flex items-center justify-center border-t pt-4 border-slate-200">
          <div className="text-xs text-center text-muted-foreground flex items-center gap-1">
            <LockKeyhole className="h-3 w-3" />
            <span>Enable Provider View to see clinical details</span>
          </div>
        </div>
      )}
    </CardContent>
  );
};

export default MedicationCardContent;
